import React, { useState, useEffect } from 'react';
import { Lock, Save, X, Check, AlertCircle } from 'lucide-react';

const AdminPanel = ({ isPreAuthenticated = false, onLogout = null }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(isPreAuthenticated);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  const [activeTab, setActiveTab] = useState('statues');
  const [statues, setStatues] = useState([]);
  const [lightings, setLightings] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [message, setMessage] = useState('');
  const [expandedInventory, setExpandedInventory] = useState({});
  const [inventoryState, setInventoryState] = useState({});

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      const credentials = btoa(`${username}:${password}`);
      const response = await fetch(`${apiUrl}/api/admin/verify`, {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      });

      if (response.ok) {
        localStorage.setItem('adminAuth', credentials);
        setIsAuthenticated(true);
        loadProducts();
      } else {
        setAuthError('Invalid credentials');
      }
    } catch (error) {
      setAuthError('Login failed');
    }
  };

  const loadProducts = async () => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth) return;

    try {
      // Load statues
      const statuesRes = await fetch(`${apiUrl}/api/admin/products/statues`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (statuesRes.ok) {
        const statuesData = await statuesRes.json();
        setStatues(statuesData);
      }

      // Load lightings
      const lightingsRes = await fetch(`${apiUrl}/api/admin/products/lightings`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (lightingsRes.ok) {
        const lightingsData = await lightingsRes.json();
        setLightings(lightingsData);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleUpdate = async (productId, productType) => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth || !editingProduct) return;

    try {
      const response = await fetch(
        `${apiUrl}/api/admin/products/${productType}/${productId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editingProduct)
        }
      );

      if (response.ok) {
        setMessage('Product updated successfully!');
        setEditingProduct(null);
        loadProducts();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Update failed');
    }
  };

  const toggleSold = async (productId, productType) => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth) return;

    try {
      const response = await fetch(
        `${apiUrl}/api/admin/products/${productType}/${productId}/toggle-sold`,
        {
          method: 'POST',
          headers: { 'Authorization': `Basic ${auth}` }
        }
      );

      if (response.ok) {
        setMessage('Status updated!');
        loadProducts();
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (error) {
      setMessage('Update failed');
    }
  };


  const toggleInventoryExpansion = (productId) => {
    setExpandedInventory(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleInventoryChange = (productId, variantName, value) => {
    setInventoryState(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [variantName]: parseInt(value) || 0
      }
    }));
  };

  const saveInventory = async (productId, type) => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth) return;

    try {
      // Map display name to actual collection name
      const collection = type === 'statues' ? 'statues' : 'lightings';
      const variants = inventoryState[productId];

      if (!variants || Object.keys(variants).length === 0) {
        setMessage('No inventory changes to save');
        return;
      }

      // Check if this is a simple product without variants
      if (variants['_default'] !== undefined) {
        // Use the simple stock update endpoint
        const response = await fetch(
          `${apiUrl}/api/admin/products/${collection}/${productId}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Basic ${auth}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ stock: variants['_default'] })
          }
        );

        if (response.ok) {
          setMessage('✅ Stock updated');
          setInventoryState(prev => {
            const newState = { ...prev };
            delete newState[productId];
            return newState;
          });
          loadProducts();
          setTimeout(() => setMessage(''), 2000);
        } else {
          setMessage('Failed to update stock');
        }
        return;
      }

      // Regular variant updates
      const variantUpdates = Object.entries(variants).map(([variantName, stock]) => ({
        variant_name: variantName,
        stock: stock
      }));

      const response = await fetch(
        `${apiUrl}/api/admin/products/${collection}/inventory/${productId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ variants: variantUpdates })
        }
      );

      if (response.ok) {
        setMessage('✅ Inventory updated');
        setInventoryState(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
        loadProducts();
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage('Failed to update inventory');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const toggleVariantSoldManual = async (productId, variantName, type) => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth) return;

    try {
      // Map display name to actual collection name
      const collection = type === 'statues' ? 'statues' : 'lightings';

      const response = await fetch(
        `${apiUrl}/api/admin/products/${collection}/${productId}/variant/${variantName}/toggle-sold`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setMessage('✅ Variant status updated');
        loadProducts();
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage('Failed to toggle variant');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const toggleProductSold = async (productId) => {
    const auth = localStorage.getItem('adminAuth');
    if (!auth) return;

    try {
      const response = await fetch(
        `${apiUrl}/api/admin/products/statues/${productId}/toggle-sold`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setMessage('✅ Product status updated');
        loadProducts();
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage('Failed to toggle product status');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const getStockStatus = (stock, threshold = 5) => {
    if (stock === 0) return { label: '🔴 Out of Stock', color: 'red' };
    if (stock <= threshold) return { label: '🟡 Low Stock', color: 'yellow' };
    return { label: '🟢 In Stock', color: 'green' };
  };


  useEffect(() => {
    if (isPreAuthenticated) {
      loadProducts();
    } else {
      const auth = localStorage.getItem('adminAuth');
      if (auth) {
        setIsAuthenticated(true);
        loadProducts();
      }
    }
  }, [isPreAuthenticated]);

  if (!isAuthenticated && !isPreAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 bg-[hsl(45,100%,70%)] rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-black" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2">Admin Panel</h1>
          <p className="text-gray-600 text-center mb-8">Artourius Design</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                required
              />
            </div>

            {authError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-[hsl(45,100%,70%)] text-black font-bold py-3 rounded-lg hover:bg-[hsl(45,100%,60%)] transition-colors duration-300"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  const products = activeTab === 'statues' ? statues : lightings;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
              <p className="text-gray-600 mt-1">Artourius Design Admin Panel</p>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('adminAuth');
                setIsAuthenticated(false);
              }}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800">{message}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('statues')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'statues'
                ? 'bg-[hsl(45,100%,70%)] text-black'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Statues ({statues.length})
          </button>
          <button
            onClick={() => setActiveTab('lightings')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'lightings'
                ? 'bg-[hsl(45,100%,70%)] text-black'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Lightings ({lightings.length})
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-md p-6">
              {editingProduct?.id === product.id ? (
                <div className="space-y-4">
                  <input
                    className="w-full px-4 py-2 border rounded-lg text-gray-900 bg-white"
                    value={editingProduct.name || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                    placeholder="Product Name"
                  />
                  
                  <textarea
                    className="w-full px-4 py-2 border rounded-lg text-gray-900 bg-white"
                    value={editingProduct.description || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                    rows="3"
                    placeholder="Description"
                  />
                  
                  <input
                    className="w-full px-4 py-2 border rounded-lg text-gray-900 bg-white"
                    value={editingProduct.price || ''}
                    onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                    placeholder="Price"
                  />

                  {activeTab === 'statues' && (
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        className="px-4 py-2 border rounded-lg text-gray-900 bg-white"
                        value={editingProduct.height || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, height: e.target.value})}
                        placeholder="Height"
                      />
                      <input
                        className="px-4 py-2 border rounded-lg text-gray-900 bg-white"
                        value={editingProduct.width || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, width: e.target.value})}
                        placeholder="Width"
                      />
                      <input
                        className="px-4 py-2 border rounded-lg text-gray-900 bg-white"
                        value={editingProduct.depth || ''}
                        onChange={(e) => setEditingProduct({...editingProduct, depth: e.target.value})}
                        placeholder="Depth"
                      />
                    </div>
                  )}

                  {/* Color Variants Management (Statues Only) */}
                  {activeTab === 'statues' && (
                    <div className="space-y-4 pt-4 border-t">
                      <h4 className="font-bold text-sm uppercase text-gray-700">Color Variants</h4>
                      
                      {/* Enable/Disable Toggle */}
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editingProduct.hasColorVariants || false}
                          onChange={(e) => setEditingProduct({
                            ...editingProduct,
                            hasColorVariants: e.target.checked,
                            colorOptions: e.target.checked ? (editingProduct.colorOptions || []) : []
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Enable Color Selection</span>
                      </label>

                      {/* Color Options (only show if enabled) */}
                      {editingProduct.hasColorVariants && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Available Colors
                          </label>
                          
                          {/* List existing colors */}
                          <div className="flex flex-wrap gap-2">
                            {(editingProduct.colorOptions || []).map((color, index) => (
                              <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                                <span className="text-sm">{color}</span>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const newColors = [...(editingProduct.colorOptions || [])];
                                    newColors.splice(index, 1);
                                    setEditingProduct({...editingProduct, colorOptions: newColors});
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Add new color */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Enter color name"
                              className="flex-1 px-3 py-2 border rounded-lg text-sm text-gray-900 bg-white"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const colorName = e.target.value.trim();
                                  if (colorName && !(editingProduct.colorOptions || []).includes(colorName)) {
                                    setEditingProduct({
                                      ...editingProduct,
                                      colorOptions: [...(editingProduct.colorOptions || []), colorName]
                                    });
                                    e.target.value = '';
                                  }
                                }
                              }}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                const input = e.target.previousSibling;
                                const colorName = input.value.trim();
                                if (colorName && !(editingProduct.colorOptions || []).includes(colorName)) {
                                  setEditingProduct({
                                    ...editingProduct,
                                    colorOptions: [...(editingProduct.colorOptions || []), colorName]
                                  });
                                  input.value = '';
                                }
                              }}
                              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600"
                            >
                              Add Color
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdate(product.id, activeTab)}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                    >
                      <Save className="w-4 h-4" /> Save
                    </button>
                    <button
                      onClick={() => setEditingProduct(null)}
                      className="flex-1 flex items-center justify-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-4 mb-4">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{product.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                      <p className="text-lg font-bold text-[hsl(45,100%,70%)] mt-2">{product.price}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-4">
                    {activeTab === 'statues' && (
                      <span>{product.height} × {product.width} × {product.depth}</span>
                    )}
                    <span
                      className={`px-2 py-1 rounded ${
                        product.sold ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {product.sold ? 'SOLD' : 'Available'}
                    </span>
                  </div>

                  {/* Inventory Management Section */}
                  {(product.sizes || product.colorVariants || activeTab === 'statues') && (
                    <div className="mt-4 mb-4 bg-purple-50 rounded-lg border border-purple-200 p-3">
                      <button
                        onClick={() => toggleInventoryExpansion(product.id)}
                        className="w-full flex items-center justify-between text-sm font-semibold text-purple-700 hover:text-purple-900"
                      >
                        <span>📦 Inventory ({Object.keys(product.sizes || product.colorVariants || {}).length} variants)</span>
                        <span>{expandedInventory[product.id] ? '▲' : '▼'}</span>
                      </button>
                      
                      {expandedInventory[product.id] && (
                        <div className="mt-3 space-y-2">
                          {/* For sculptures without variants - show single stock input */}
                          {!product.sizes && !product.colorVariants && activeTab === 'statues' && (
                            <div className="p-2 bg-white rounded border">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-gray-800">Product Stock</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  product.sold ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {product.sold ? '🔴 SOLD' : '🟢 Available'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-gray-700">Stock:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={inventoryState[product.id]?.['_default'] ?? (product.stock !== undefined ? product.stock : 1)}
                                  onChange={(e) => handleInventoryChange(product.id, '_default', e.target.value)}
                                  className="w-16 px-2 py-1 border rounded text-center text-gray-900 bg-white"
                                />
                                <button
                                  onClick={() => toggleProductSold(product.id)}
                                  className={`px-2 py-1 rounded text-xs ${
                                    product.sold
                                      ? 'bg-green-500 text-white hover:bg-green-600'
                                      : 'bg-red-500 text-white hover:bg-red-600'
                                  }`}
                                >
                                  {product.sold ? 'Mark Available' : 'Mark Sold'}
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {product.sizes && Object.entries(product.sizes).map(([sizeName, sizeData]) => {
                            const isSold = sizeData.sold || false;
                            const isManualOverride = sizeData.manualSoldOverride || false;
                            // Default stock is 1, but if sold (and no manual override), show 0
                            const stock = sizeData.stock !== undefined ? sizeData.stock : (isSold && !isManualOverride ? 0 : 1);
                            const stockStatus = getStockStatus(stock);
                            const currentValue = inventoryState[product.id]?.[sizeName] ?? stock;
                            
                            return (
                              <div key={sizeName} className="p-2 bg-white rounded border">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-semibold text-gray-800">Size: {sizeName}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    isSold && isManualOverride ? 'bg-orange-100 text-orange-700' :
                                    isSold ? 'bg-red-100 text-red-700' :
                                    stockStatus.color === 'green' ? 'bg-green-100 text-green-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {isSold && isManualOverride ? '🔴 SOLD (Manual)' : isSold ? '🔴 Out' : stockStatus.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-700">Stock:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={currentValue}
                                    onChange={(e) => handleInventoryChange(product.id, sizeName, e.target.value)}
                                    className="w-16 px-2 py-1 border rounded text-center text-gray-900 bg-white"
                                  />
                                  <button
                                    onClick={() => toggleVariantSoldManual(product.id, sizeName, activeTab)}
                                    disabled={stock === 0 && !isManualOverride}
                                    className={`px-2 py-1 rounded text-xs ${
                                      stock === 0 && !isManualOverride
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : isSold
                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                        : 'bg-red-500 text-white hover:bg-red-600'
                                    }`}
                                  >
                                    {stock === 0 && !isManualOverride ? 'Auto-Sold' : isSold ? 'Mark Available' : 'Mark Sold'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          
                          {!product.sizes && product.colorVariants && Object.entries(product.colorVariants).map(([colorName, colorData]) => {
                            const isSold = colorData.sold || false;
                            const isManualOverride = colorData.manualSoldOverride || false;
                            // Default stock is 1, but if sold (and no manual override), show 0
                            const stock = colorData.stock !== undefined ? colorData.stock : (isSold && !isManualOverride ? 0 : 1);
                            const stockStatus = getStockStatus(stock);
                            const currentValue = inventoryState[product.id]?.[colorName] ?? stock;
                            
                            return (
                              <div key={colorName} className="p-2 bg-white rounded border">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-semibold text-gray-800">Color: {colorName}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    isSold && isManualOverride ? 'bg-orange-100 text-orange-700' :
                                    isSold ? 'bg-red-100 text-red-700' :
                                    stockStatus.color === 'green' ? 'bg-green-100 text-green-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {isSold && isManualOverride ? '🔴 SOLD (Manual)' : isSold ? '🔴 Out' : stockStatus.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-700">Stock:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={currentValue}
                                    onChange={(e) => handleInventoryChange(product.id, colorName, e.target.value)}
                                    className="w-16 px-2 py-1 border rounded text-center text-gray-900 bg-white"
                                  />
                                  <button
                                    onClick={() => toggleVariantSoldManual(product.id, colorName, activeTab)}
                                    disabled={stock === 0 && !isManualOverride}
                                    className={`px-2 py-1 rounded text-xs ${
                                      stock === 0 && !isManualOverride
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : isSold
                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                        : 'bg-red-500 text-white hover:bg-red-600'
                                    }`}
                                  >
                                    {stock === 0 && !isManualOverride ? 'Auto-Sold' : isSold ? 'Mark Available' : 'Mark Sold'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          
                          <button
                            onClick={() => saveInventory(product.id, activeTab)}
                            disabled={!inventoryState[product.id] || Object.keys(inventoryState[product.id] || {}).length === 0}
                            className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                          >
                            💾 Save Inventory Changes
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingProduct(product)}
                      className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleSold(product.id, activeTab)}
                      className={`flex-1 px-4 py-2 rounded-lg ${
                        product.sold
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-red-500 hover:bg-red-600'
                      } text-white`}
                    >
                      Mark as {product.sold ? 'Available' : 'Sold'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
